import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Button,
  Stack,
  Tabs,
  Tab,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Select,
  MenuItem,
  TextField,
  Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Services from 'src/services/Services';

const VEHICLE_TYPES = [
  { key: 'pet-car', label: 'Evcil Hayvan Aracı' },
  { key: 'kid-car', label: 'Çocuk Arabası' },
];

const STATUS_OPTIONS = [
  { value: 'available', label: 'Uygun' },
  { value: 'in-use', label: 'Dolu' },
];

const padBarcode = (num) => String(num).padStart(7, '0');

const normalizeInventory = (inventory) => {
  if (!inventory || typeof inventory !== 'object') return {};
  const next = {};
  Object.entries(inventory).forEach(([key, list]) => {
    if (!Array.isArray(list)) {
      next[key] = [];
      return;
    }
    next[key] = list.map((item, idx) => ({
      barcode: item?.barcode ?? padBarcode(idx + 1),
      status: item?.status ?? 'available',
      color: item?.color ?? '',
    }));
  });
  return next;
};

const getNextBarcode = (list) => {
  if (!Array.isArray(list) || list.length === 0) return padBarcode(1);
  const maxNum = list.reduce((max, item) => {
    const n = Number(item?.barcode);
    if (!Number.isNaN(n)) return Math.max(max, n);
    return max;
  }, 0);
  return padBarcode(maxNum + 1);
};

export default function VehicleInventoryManageModal({
  open,
  mallID,
  mallName,
  onClose,
  onSaved,
}) {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [vehicleInventory, setVehicleInventory] = useState({});

  const selectedKey = VEHICLE_TYPES[activeTab]?.key ?? VEHICLE_TYPES[0].key;

  const token = useMemo(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user?.token || '';
  }, []);

  useEffect(() => {
    if (!open || !mallID || !token) return;
    setLoading(true);
    Services.GetMall(token, mallID)
      .then((res) => {
        if (res?.data?.status) {
          const inv = res?.data?.result?.vehicleInventory || {};
          setVehicleInventory(normalizeInventory(inv));
        }
      })
      .catch((err) => console.error('VehicleInventoryManageModal GetMall error', err))
      .finally(() => setLoading(false));
  }, [open, mallID, token]);

  const rows = Array.isArray(vehicleInventory?.[selectedKey])
    ? vehicleInventory[selectedKey]
    : [];

  const handleStatusChange = (idx, value) => {
    setVehicleInventory((prev) => {
      const next = { ...prev };
      const list = Array.isArray(next[selectedKey]) ? [...next[selectedKey]] : [];
      list[idx] = { ...list[idx], status: value };
      next[selectedKey] = list;
      return next;
    });
  };

  const handleColorChange = (idx, value) => {
    setVehicleInventory((prev) => {
      const next = { ...prev };
      const list = Array.isArray(next[selectedKey]) ? [...next[selectedKey]] : [];
      list[idx] = { ...list[idx], color: value };
      next[selectedKey] = list;
      return next;
    });
  };

  const handleAddVehicle = () => {
    setVehicleInventory((prev) => {
      const next = { ...prev };
      const list = Array.isArray(next[selectedKey]) ? [...next[selectedKey]] : [];
      list.push({
        barcode: getNextBarcode(list),
        status: 'available',
        color: '',
      });
      next[selectedKey] = list;
      return next;
    });
  };

  const handleRemoveVehicle = (idx) => {
    setVehicleInventory((prev) => {
      const next = { ...prev };
      const list = Array.isArray(next[selectedKey]) ? [...next[selectedKey]] : [];
      list.splice(idx, 1);
      next[selectedKey] = list;
      return next;
    });
  };

  const handleSave = async () => {
    if (!token || !mallID) return;
    setLoading(true);
    try {
      await Services.UpdateMallVehicleInventory(token, mallID, vehicleInventory);
      onSaved?.(vehicleInventory);
      onClose?.();
    } catch (err) {
      console.error('VehicleInventoryManageModal update error', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h6">Araç Yönetimi</Typography>
          <Typography variant="body2" color="text.secondary">
            {mallName || 'AVM'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Tabs
            value={activeTab}
            onChange={(_, value) => setActiveTab(value)}
            aria-label="Araç tipleri"
          >
            {VEHICLE_TYPES.map((t) => (
              <Tab key={t.key} label={t.label} />
            ))}
          </Tabs>

          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="body2" color="text.secondary">
              Araç no otomatik oluşturulur.
            </Typography>
            <Button variant="outlined" onClick={handleAddVehicle} disabled={loading}>
              Araç Ekle
            </Button>
          </Stack>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 140 }}>Araç No</TableCell>
                <TableCell sx={{ width: 180 }}>Statü</TableCell>
                <TableCell>Renk</TableCell>
                <TableCell sx={{ width: 80 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, idx) => (
                <TableRow key={`${row.barcode}-${idx}`}>
                  <TableCell>{row.barcode}</TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      value={row.status || 'available'}
                      onChange={(e) => handleStatusChange(idx, e.target.value)}
                      fullWidth
                    >
                      {STATUS_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      placeholder="Renk"
                      value={row.color || ''}
                      onChange={(e) => handleColorChange(idx, e.target.value)}
                      fullWidth
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      color="error"
                      size="small"
                      onClick={() => handleRemoveVehicle(idx)}
                    >
                      Sil
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography variant="body2" color="text.secondary">
                      Kayıtlı araç yok.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Vazgeç</Button>
        <Button variant="contained" onClick={handleSave} disabled={loading}>
          Kaydet
        </Button>
      </DialogActions>
    </Dialog>
  );
}

VehicleInventoryManageModal.propTypes = {
  open: PropTypes.bool.isRequired,
  mallID: PropTypes.string,
  mallName: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func,
};
